import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { ExternalLink, ArrowLeft, User } from 'lucide-react'
import { hydrateCandidates } from '../engine/candidateData'
import { supabase } from '../lib/supabase'
import { CATEGORIES, STANCE_QUESTIONS, computeMatch, loadUserAnswers, hasCompletedQuestionnaire } from '../engine/matchEngine'
import MatchBar from '../components/MatchBar'
import InfluenceMap from '../components/InfluenceMap'

const partyColors = {
  Democratic:  'bg-blue-100 text-blue-800',
  Republican:  'bg-red-100 text-red-800',
  Independent: 'bg-purple-100 text-purple-800',
}

const confidenceLabel = score =>
  score >= 0.95 ? { label: 'Very High', color: 'text-green-600' } :
  score >= 0.85 ? { label: 'High',      color: 'text-blue-600'  } :
  score >= 0.70 ? { label: 'Medium',    color: 'text-yellow-600'} :
                  { label: 'Low',       color: 'text-red-500'   }

export default function CandidateDetail() {
  const { id } = useParams()
  const [candidate, setCandidate] = useState(null)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [fetchError, setFetchError] = useState('')

  useEffect(() => {
    if (!id) { setNotFound(true); setLoading(false); return }

    let cancelled = false
    setLoading(true)
    setNotFound(false)
    setFetchError('')
    async function fetchCandidate() {
      try {
        // Try matching by id first, then fall back to external_id
        let { data, error } = await supabase
          .from('candidates')
          .select('*')
          .eq('id', id)
          .single()

        if (error && !['PGRST116', '22P02'].includes(error.code)) throw error
        if (error || !data) {
          // Fallback: try external_id (covers slug-style URLs)
          const result = await supabase
            .from('candidates')
            .select('*')
            .eq('external_id', id)
            .single()
          data = result.data
          error = result.error
        }

        if (cancelled) return
        if (error && error.code !== 'PGRST116') throw error
        if (error || !data) {
          setNotFound(true)
        } else {
          const hydrated = await hydrateCandidates([data])
          if (!cancelled) setCandidate(hydrated[0])
        }
      } catch (err) {
        console.error('[CandidateDetail] fetch error:', err)
        if (!cancelled) setFetchError('Could not load candidate evidence. Please try again.')
      }
      if (!cancelled) setLoading(false)
    }
    fetchCandidate()
    return () => { cancelled = true }
  }, [id])

  if (loading) {
    return (
      <div className="section text-center">
        <p className="text-civitas-silver">Loading candidate...</p>
      </div>
    )
  }

  if (fetchError) return <div className="section"><p role="alert">{fetchError}</p><Link to="/results">Back to My Matches</Link></div>

  if (notFound || !candidate) {
    return (
      <div className="section text-center">
        <h1 className="font-display text-2xl text-civitas-navy">Candidate not found</h1>
        <Link to="/results" className="btn-primary mt-4 inline-block">← Back to My Matches</Link>
      </div>
    )
  }

  const completed = hasCompletedQuestionnaire()
  const { weights, stances } = loadUserAnswers()
  const { score, breakdown } = completed ? computeMatch(weights, stances, candidate) : { score: null, breakdown: {} }

  const evidenceLedger = Array.isArray(candidate.evidence_ledger) ? candidate.evidence_ledger : []

  return (
    <div className="section max-w-4xl">
      {/* Back */}
      <Link to="/results" className="flex items-center gap-1 text-sm text-civitas-silver hover:text-civitas-navy mb-6">
        <ArrowLeft className="h-4 w-4" /> Back to My Matches
      </Link>

      {/* Hero */}
      <div className="card mb-6">
        <div className="flex flex-col md:flex-row gap-6">
          <div className="flex h-20 w-20 items-center justify-center rounded-full bg-civitas-light text-civitas-blue shrink-0">
            <User className="h-10 w-10" />
          </div>
          <div className="flex-1">
            <div className="flex flex-wrap items-start gap-2 mb-2">
              <h1 className="font-display text-3xl text-civitas-navy">{candidate.name}</h1>
              <span className={`badge mt-1 ${partyColors[candidate.party] ?? 'bg-gray-100 text-gray-600'}`}>
                {candidate.party}
              </span>
            </div>
            <p className="text-civitas-silver mb-1">{candidate.office} · {candidate.district} · {candidate.state}</p>
            <p className="text-sm text-gray-600 mt-3">{candidate.bio}</p>
            {candidate.campaign_url && (
              <a
                href={candidate.campaign_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 mt-3 text-sm text-civitas-blue hover:underline"
              >
                Campaign Website <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </div>
          {completed && score !== null && (
            <div className="flex flex-col items-center justify-center bg-civitas-light rounded-xl p-4 min-w-[110px]">
              <div className="text-4xl font-bold text-civitas-navy">{score}%</div>
              <div className="text-xs text-civitas-silver mt-1">Your Match</div>
              <MatchBar score={score} animate height="h-2" />
            </div>
          )}
        </div>
      </div>

      {/* Match breakdown */}
      {completed && (
        <div className="card mb-6">
          <h2 className="font-semibold text-civitas-navy mb-4">Match Breakdown by Category</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {CATEGORIES.map(cat => (
              <MatchBar
                key={cat.id}
                score={breakdown[cat.id] ?? null}
                label={cat.label}
                height="h-2"
              />
            ))}
          </div>
        </div>
      )}

      {/* Stance positions */}
      <div className="card mb-6">
        <h2 className="font-semibold text-civitas-navy mb-4">Policy Positions</h2>
        {Object.keys(candidate.position_vector || {}).length === 0 ? (
          <p className="text-sm text-civitas-silver italic">
            Position data not yet available for this candidate. We&apos;re working on it.
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {STANCE_QUESTIONS.map(q => {
              const val = candidate.position_vector?.[q.id]
              if (val === undefined) return null
              const pct = Math.round(val * 100)
              return (
                <div key={q.id}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-700">{q.label}</span>
                    <span className={`font-medium text-xs ${
                      pct >= 60 ? 'text-blue-600' : pct <= 40 ? 'text-red-500' : 'text-gray-500'
                    }`}>
                      {pct >= 60 ? 'Supports' : pct <= 40 ? 'Opposes' : 'Moderate'}
                    </span>
                  </div>
                  <div className="w-full h-2 bg-gray-100 rounded-full">
                    <div
                      className={`h-2 rounded-full ${pct >= 60 ? 'bg-blue-400' : pct <= 40 ? 'bg-red-400' : 'bg-gray-400'}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Evidence Ledger */}
      {evidenceLedger.length > 0 && (
        <div className="card mb-6">
          <h2 className="font-semibold text-civitas-navy mb-4">Evidence Ledger</h2>
          <p className="text-xs text-civitas-silver mb-4">
            Every position is backed by verifiable public record. Confidence scores reflect source quality and recency.
          </p>
          <div className="flex flex-col gap-4">
            {evidenceLedger.map((ev, i) => {
              const conf = confidenceLabel(ev.confidence_score)
              return (
                <div key={i} className="border border-gray-100 rounded-lg p-4">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <span className="badge bg-civitas-light text-civitas-blue text-xs uppercase tracking-wide">
                      {ev.topic?.replace(/_/g, ' ')}
                    </span>
                    <span className={`text-xs font-medium ${conf.color}`}>
                      Confidence: {conf.label} ({Math.round(ev.confidence_score * 100)}%)
                    </span>
                  </div>
                  <p className="text-sm text-gray-700 mt-2">{ev.score_justification}</p>
                  {ev.source_url && (
                    <a
                      href={ev.source_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 mt-2 text-xs text-civitas-blue hover:underline"
                    >
                      View Source <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Influence map */}
      <InfluenceMap candidate={candidate} />

      {/* Actions */}
      <div className="flex gap-3 mt-6">
        <Link to={`/compare?a=${candidate.id}`} className="btn-primary">Compare This Candidate</Link>
        <Link to="/results" className="btn-secondary">My Matches</Link>
      </div>
    </div>
  )
}
